import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getTerminology } from '../utils/terminology';
import { useSettings } from '../context/SettingsContext';

const Sidebar = ({ logo, collapsed, toggleCollapse, onItemClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return "/logo.svg"; // Default to new SVG logo
    if (logoPath.startsWith('http')) return logoPath;
    return `${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk/"}${logoPath.startsWith('/') ? '' : '/'}${logoPath}`;
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  const [user, setUser] = React.useState(JSON.parse(localStorage.getItem('user')));

  React.useEffect(() => {
    const handleUpdate = (e) => {
      setUser(e.detail || JSON.parse(localStorage.getItem('user')));
    };

    window.addEventListener('profileUpdated', handleUpdate);
    return () => window.removeEventListener('profileUpdated', handleUpdate);
  }, []);

  // Robust check for Super Admin
  const isSuperAdmin =
    user?.role === 'super_admin' ||
    user?.data?.role === 'super_admin' || // Handle wrapped response
    user?.email === 'superadmin@webhunnar.com' ||
    user?.data?.email === 'superadmin@webhunnar.com';

  const terminology = getTerminology(settings.school_type || user?.school?.type || user?.data?.school?.type);

  const userRole = user?.role || user?.data?.role;
  const staffPerms = user?.staff?.permissions || user?.data?.staff?.permissions || [];
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';

  const canManageFees = isAdmin || staffPerms.includes('manage_fees');
  const canManageExpenses = isAdmin || staffPerms.includes('manage_expenses');
  const canManageInventory = isAdmin || staffPerms.includes('manage_inventory');
  const canManageExams = isAdmin || staffPerms.includes('manage_exams');
  const canManageStaff = isAdmin;

  return (
    <div className={`sidebar-container ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-brand">
        <Link to="/" onClick={onItemClick} className="d-flex align-items-center justify-content-center w-100">
          {collapsed ? (
            <img src="/favicon.svg" alt="Logo" className="sidebar-logo-sm" style={{ width: '40px', height: 'auto' }} />
          ) : (
            <img src={getLogoUrl(logo)} alt="Logo" className="sidebar-logo mb-2" style={{ maxWidth: '90%', maxHeight: '100px', objectFit: 'contain' }} />
          )}
        </Link>
      </div>

      {!collapsed && (settings.academic_session || settings.academic_year) && (
        <div className="text-center px-3 mb-3 fade-in">
          <span 
            className="badge bg-light-subtle text-secondary border px-3 py-2 rounded-pill fw-semibold" 
            style={{ fontSize: '0.72rem', letterSpacing: '0.3px', background: 'rgba(255,255,255,0.85)' }}
          >
            <i className="bi bi-calendar-event text-primary me-1"></i>
            Session: {settings.academic_session || settings.academic_year}
          </span>
        </div>
      )}

      {collapsed && (settings.academic_session || settings.academic_year) && (
        <div 
          className="text-center mb-3 text-secondary fade-in cursor-pointer" 
          title={`Academic Session: ${settings.academic_session || settings.academic_year}`}
        >
          <i className="bi bi-calendar-event fs-5"></i>
        </div>
      )}

      <div className="sidebar-menu">
        {isSuperAdmin && (
          <>
            <Link to="/super-admin" className={`sidebar-item ${isActive('/super-admin') === 'active' && !window.location.pathname.includes('/tickets') ? 'bg-primary text-white' : ''}`} title="Super Admin" onClick={onItemClick}>
              <span className="item-icon"><i className={`bi bi-shield-lock-fill ${isActive('/super-admin') === 'active' && !window.location.pathname.includes('/tickets') ? 'text-white' : 'text-danger'}`}></i></span>
              <span className={`item-label fw-bold ${isActive('/super-admin') === 'active' && !window.location.pathname.includes('/tickets') ? 'text-white' : 'text-danger'}`}>Super Admin</span>
            </Link>
            <Link to="/super-admin/tickets" className={`sidebar-item ${isActive('/super-admin/tickets') === 'active' ? 'bg-primary text-white' : ''}`} title="Global Tickets" onClick={onItemClick}>
              <span className="item-icon"><i className={`bi bi-headset ${isActive('/super-admin/tickets') === 'active' ? 'text-white' : 'text-danger'}`}></i></span>
              <span className={`item-label fw-bold ${isActive('/super-admin/tickets') === 'active' ? 'text-white' : 'text-danger'}`}>Global Tickets</span>
            </Link>
          </>
        )}

        {!isSuperAdmin && (
          <>
            <Link to="/" className={`sidebar-item ${isActive('/')}`} title="Dashboard" onClick={onItemClick}>
              <span className="item-icon"><i className="bi bi-grid-fill"></i></span>
              <span className="item-label">Dashboard</span>
            </Link>

            {/* Admin & Staff Access */}
            {['admin', 'staff'].includes(user?.role) && (
              <>
                <Link to="/enquiries" className={`sidebar-item ${isActive('/enquiries')}`} title="Enquiries" onClick={onItemClick}>
                  <span className="item-icon"><i className="bi bi-chat-square-text-fill"></i></span>
                  <span className="item-label">Enquiries</span>
                </Link>
                <Link to="/students" className={`sidebar-item ${isActive('/students')}`} title={terminology.students} onClick={onItemClick}>
                  <span className="item-icon"><i className="bi bi-people-fill"></i></span>
                  <span className="item-label">{terminology.students}</span>
                </Link>
                <Link to="/courses" className={`sidebar-item ${isActive('/courses')}`} title={terminology.courses} onClick={onItemClick}>
                  <span className="item-icon"><i className="bi bi-book-fill"></i></span>
                  <span className="item-label">{terminology.courses}</span>
                </Link>
                {canManageFees && (
                  <Link to="/fees" className={`sidebar-item ${isActive('/fees')}`} title="Fees" onClick={onItemClick}>
                    <span className="item-icon"><i className="bi bi-credit-card-fill"></i></span>
                    <span className="item-label">Fees</span>
                  </Link>
                )}
              </>
            )}

            {/* Shared Modules with Permissions */}
            {canManageExpenses && (
              <Link to="/expenses" className={`sidebar-item ${isActive('/expenses')}`} title="Expenses" onClick={onItemClick}>
                <span className="item-icon"><i className="bi bi-wallet-fill"></i></span>
                <span className="item-label">Expenses</span>
              </Link>
            )}

            {isAdmin && (
              <Link to="/staff" className={`sidebar-item ${isActive('/staff')}`} title="Staff" onClick={onItemClick}>
                <span className="item-icon"><i className="bi bi-person-badge-fill"></i></span>
                <span className="item-label">Staff</span>
              </Link>
            )}

            {/* Shared Modules */}
            <Link to="/attendance" className={`sidebar-item ${isActive('/attendance')}`} title="Attendance" onClick={onItemClick}>
              <span className="item-icon"><i className="bi bi-calendar-check-fill"></i></span>
              <span className="item-label">Attendance</span>
            </Link>
            <Link to="/attendance/quick-scan" className={`sidebar-item ${isActive('/attendance/quick-scan')}`} title="Quick Scan" onClick={onItemClick}>
              <span className="item-icon"><i className="bi bi-upc-scan text-success"></i></span>
              <span className="item-label text-success">Quick Scan</span>
            </Link>
            {canManageExams && (
              <Link to="/exams" className={`sidebar-item ${isActive('/exams')}`} title="Exams" onClick={onItemClick}>
                <span className="item-icon"><i className="bi bi-award-fill"></i></span>
                <span className="item-label">Exams</span>
              </Link>
            )}
            <Link to="/lms" className={`sidebar-item ${isActive('/lms')}`} title="LMS" onClick={onItemClick}>
              <span className="item-icon"><i className="bi bi-laptop-fill"></i></span>
              <span className="item-label">LMS</span>
            </Link>

            {/* Inventory Access */}
            {canManageInventory && (
              <Link to="/inventory" className={`sidebar-item ${isActive('/inventory')}`} title="Inventory" onClick={onItemClick}>
                <span className="item-icon"><i className="bi bi-box-seam-fill"></i></span>
                <span className="item-label">Inventory</span>
              </Link>
            )}

            <a href="https://appy.pk/docs-lms.php" target="_blank" rel="noopener noreferrer" className="sidebar-item" title="Help & Documentation">
              <span className="item-icon"><i className="bi bi-question-circle-fill text-info"></i></span>
              <span className="item-label">Documentation</span>
            </a>

            {isAdmin && (
              <>
                <Link to="/support" className={`sidebar-item ${isActive('/support')}`} title="Support Tickets" onClick={onItemClick}>
                  <span className="item-icon"><i className="bi bi-headset text-warning"></i></span>
                  <span className="item-label">Support</span>
                </Link>
                <Link to="/activity-logs" className={`sidebar-item ${isActive('/activity-logs')}`} title="Activity Logs" onClick={onItemClick}>
                  <span className="item-icon"><i className="bi bi-clock-history text-info"></i></span>
                  <span className="item-label">Activity Logs</span>
                </Link>
                <Link to="/profile" className={`sidebar-item ${isActive('/profile')}`} title="Settings" onClick={onItemClick}>
                  <span className="item-icon"><i className="bi bi-gear-fill"></i></span>
                  <span className="item-label">Settings</span>
                </Link>
              </>
            )}
          </>
        )}

        <div className="mt-auto pt-4 border-top">
          {/* Collapse Toggle Button (Desktop Only) */}
          <button onClick={toggleCollapse} className="sidebar-toggler d-none d-lg-flex mb-2" title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}>
            <span className="item-icon"><i className={`bi ${collapsed ? 'bi-arrow-bar-right' : 'bi-arrow-bar-left'}`}></i></span>
            <span className="sidebar-toggler-text">Collapse Sidebar</span>
          </button>

          <button onClick={handleLogout} className="sidebar-item text-secondary border-0 bg-transparent w-100 text-start" title="Logout">
            <span className="item-icon"><i className="bi bi-box-arrow-right"></i></span>
            <span className="item-label">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
